import { useState, useRef, useEffect } from "react";

import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { SlikeLogo } from "@/components/SlikeLogo";
import { authApi } from "@/api/auth";
import { NetworkError } from "@/api/network";
import { useAppDispatch } from "@/store/hooks";
import { TOTPVerifyModal } from "@/components/TOTPVerifyModal";
import { handlePostLogin } from "@/utils/postLoginHandler";
import type { LoginResponseData } from "@/types/auth";

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const email = location.state?.email;
  const totpEnabledFromCheck = location.state?.totpEnabled || false;
  const loginMethod = location.state?.loginMethod || 'email';
  const redirect = location.state?.redirect;
  const redirectOrigin = location.state?.redirectOrigin;
  const service = location.state?.service;

  const [emailOtp, setEmailOtp] = useState(["", "", "", "", "", ""]);
  const [totpCode, setTotpCode] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [showTOTPModal, setShowTOTPModal] = useState(false);
  const [totpUserInfo, setTotpUserInfo] = useState<{ email: string; name: string } | null>(null);
  const [pendingEmailOtp, setPendingEmailOtp] = useState<string | null>(null);
  const [totpError, setTotpError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string>("");
  const [emailOtpError, setEmailOtpError] = useState<boolean>(false);
  const [totpInputError, setTotpInputError] = useState<boolean>(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [lockoutTimer, setLockoutTimer] = useState(0);
  const [attemptTimestamps, setAttemptTimestamps] = useState<number[]>([]);

  const emailOtpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const totpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const showTotpInput = totpEnabledFromCheck;

  useEffect(() => {
    if (!email && loginMethod === 'email') {
      navigate("/");
      return;
    }
    emailOtpRefs.current[0]?.focus();
  }, [email, loginMethod, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (lockoutTimer > 0) {
      const timer = setTimeout(() => setLockoutTimer(lockoutTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [lockoutTimer]);

  const handleOtpChange = (
    index: number,
    value: string,
    otpArray: string[],
    setOtpArray: (otp: string[]) => void,
    refs: React.MutableRefObject<(HTMLInputElement | null)[]>,
    nextRefs?: React.MutableRefObject<(HTMLInputElement | null)[]>
  ) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otpArray];
    newOtp[index] = value;
    setOtpArray(newOtp);

    if (value && index < 5) {
      refs.current[index + 1]?.focus();
    } else if (value && index === 5 && nextRefs) {
      nextRefs.current[0]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent,
    otpArray: string[],
    refs: React.MutableRefObject<(HTMLInputElement | null)[]>,
    prevRefs?: React.MutableRefObject<(HTMLInputElement | null)[]>,
    isLastInput?: boolean
  ) => {
    if (e.key === "Backspace" && !otpArray[index]) {
      if (index > 0) {
        refs.current[index - 1]?.focus();
      } else if (prevRefs) {
        prevRefs.current[5]?.focus();
      }
    } else if (e.key === "Enter" && !isLoading && lockoutTimer === 0 && attemptCount < 5) {
      e.preventDefault();
      const emailFilled = emailOtp.every(d => d !== "");
      const totpFilled = showTotpInput ? totpCode.every(d => d !== "") : true;
      
      if (emailFilled && totpFilled) {
        handleVerify();
      } else if (emailFilled && !showTotpInput) {
        handleVerify();
      }
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent,
    otpArray: string[],
    setOtpArray: (otp: string[]) => void,
    refs: React.MutableRefObject<(HTMLInputElement | null)[]>
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim().replace(/\D/g, "").slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = [...otpArray];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtpArray(newOtp);
      const nextEmptyIndex = newOtp.findIndex((d) => d === "");
      refs.current[nextEmptyIndex === -1 ? 5 : nextEmptyIndex]?.focus();
    }
  };

  const handleVerify = async (modalTotpCode?: string) => {
    // Check lockout timer
    if (lockoutTimer > 0) {
      setOtpError(`Too many attempts. Try again later after ${lockoutTimer} seconds.`);
      return;
    }

    // Check if we've exceeded 5 total attempts
    if (attemptCount >= 5) {
      setOtpError("Too many failed attempts. Please resend OTP and try with a new code.");
      return;
    }

    const emailOtpValue = modalTotpCode ? pendingEmailOtp : emailOtp.join("");
    const totpValue = modalTotpCode || (showTotpInput ? totpCode.join("") : undefined);

    if (!emailOtpValue || emailOtpValue.length !== 6) {
      setOtpError("Please enter the complete 6-digit email OTP");
      return;
    }

    if (showTotpInput && !modalTotpCode && (!totpValue || totpValue.length !== 6)) {
      setOtpError("Please enter the complete 6-digit authenticator code");
      return;
    }

    setOtpError("");
    setEmailOtpError(false);
    setTotpInputError(false);
    setIsLoading(true);
    try {
      const response = await authApi.verifyOTP({
        email,
        emailOtp: emailOtpValue,
        totpCode: totpValue || undefined,
        redirect: redirect || redirectOrigin,
        service: service
      });

      if ((response.totpRequired || response.requiresTotp) && !showTotpInput) {
        setIsLoading(false);
        setPendingEmailOtp(emailOtpValue);
        setTotpUserInfo({
          email: response.data?.email || email,
          name: response.data?.name || email.split('@')[0]
        });
        setShowTOTPModal(true);
        return;
      }

      if (response.success && response.token) {
        await handlePostLogin({
          token: response.token,
          response,
          data: response.data,
          redirect,
          redirectOrigin,
          dispatch,
          navigate,
          successMessage: response.message || "Login successful!"
        });
        return;
      }

      if (response.success && response.data?.token) {
        await handlePostLogin({
          token: response.data.token,
          response,
          data: response.data,
          redirect,
          redirectOrigin,
          dispatch,
          navigate,
          successMessage: response.message || "Login successful!"
        });
        return;
      }

      // This shouldn't happen if response is successful, but handle it just in case
      const apiErrorMsg = response.message || "Invalid email OTP. Please try again.";
      const errorMsgLower = apiErrorMsg.toLowerCase();
      const isTotpError = errorMsgLower.includes('totp') || 
                        errorMsgLower.includes('authenticator') ||
                        errorMsgLower.includes('two-factor') ||
                        errorMsgLower.includes('2fa');
      
      if (showTotpInput && emailOtpValue.length === 6 && totpValue && totpValue.length === 6) {
        // Both were provided but verification failed
        if (isTotpError) {
          setOtpError(apiErrorMsg);
          setTotpInputError(true);
          setEmailOtpError(false);
          setTotpCode(["", "", "", "", "", ""]);
          setTimeout(() => totpRefs.current[0]?.focus(), 0);
        } else {
          setOtpError(apiErrorMsg);
          setEmailOtpError(true);
          setTotpInputError(false);
          setEmailOtp(["", "", "", "", "", ""]);
          setTimeout(() => emailOtpRefs.current[0]?.focus(), 0);
        }
      } else {
        // Only OTP was provided or TOTP wasn't filled
        setOtpError(apiErrorMsg);
        setEmailOtpError(true);
        setTotpInputError(false);
        setEmailOtp(["", "", "", "", "", ""]);
        if (showTotpInput) {
          setTotpCode(["", "", "", "", "", ""]);
        }
        setTimeout(() => emailOtpRefs.current[0]?.focus(), 0);
      }
      handleFailedAttempt();
    } catch (error) {
      if (error instanceof NetworkError) {
        if (error.status === 401) {
          const apiErrorMessage = error.message;
          const errorMsg = apiErrorMessage.toLowerCase();
          
          // Check if the API error message indicates TOTP is invalid
          const isTotpInvalid = errorMsg.includes('totp') || 
                               errorMsg.includes('authenticator') ||
                               errorMsg.includes('two-factor') ||
                               errorMsg.includes('2fa');
          
          if (isTotpInvalid) {
            // TOTP is invalid - use API error message
            if (showTOTPModal) {
              setTotpError(apiErrorMessage);
            } else {
              setOtpError(apiErrorMessage);
              setTotpInputError(true);
              setEmailOtpError(false);
              setTotpCode(["", "", "", "", "", ""]);
              setTimeout(() => totpRefs.current[0]?.focus(), 0);
            }
          } else {
            // Email OTP is invalid - use API error message
            setOtpError(apiErrorMessage);
            setEmailOtpError(true);
            setTotpInputError(false);
            setEmailOtp(["", "", "", "", "", ""]);
            if (showTotpInput) {
              setTotpCode(["", "", "", "", "", ""]);
            }
            setTimeout(() => emailOtpRefs.current[0]?.focus(), 0);
          }
          handleFailedAttempt();
          return;
        }
        if (error.message.toLowerCase().includes('too many')) {
          const match = error.message.match(/(\d+)\s*seconds?/i);
          const seconds = match ? parseInt(match[1]) : 60;
          setLockoutTimer(seconds);
          setOtpError(`Too many attempts. Try again later after ${seconds} seconds.`);
          return;
        }
        handleFailedAttempt();
        setOtpError(error.message);
      } else {
        handleFailedAttempt();
        setOtpError("Verification failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFailedAttempt = () => {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;

    // Filter attempts within the last minute
    const recentAttempts = attemptTimestamps.filter(timestamp => timestamp > oneMinuteAgo);
    const newAttempts = [...recentAttempts, now];

    setAttemptTimestamps(newAttempts);
    setAttemptCount(attemptCount + 1);

    // If 3 attempts in the last minute, set lockout timer
    if (newAttempts.length >= 3) {
      setLockoutTimer(60);
    }
  };

  const handleTOTPVerify = async (code: string) => {
    setTotpError(null);
    setTotpInputError(false);
    await handleVerify(code);
    // Note: handleVerify will set isLoading to false in finally block
    // and setTotpError if there's a TOTP error
  };

  const handleTOTPCancel = () => {
    setShowTOTPModal(false);
    setTotpUserInfo(null);
    setPendingEmailOtp(null);
    setTotpError(null);
    setTotpInputError(false);
    setEmailOtp(["", "", "", "", "", ""]);
    emailOtpRefs.current[0]?.focus();
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;

    try {
      await authApi.resendOTP(email);
      setResendTimer(60);
      setEmailOtp(["", "", "", "", "", ""]);
      setTotpCode(["", "", "", "", "", ""]);
      setAttemptCount(0);
      setAttemptTimestamps([]);
      setLockoutTimer(0);
      setOtpError("");
      setEmailOtpError(false);
      setTotpInputError(false);
      emailOtpRefs.current[0]?.focus();
      toast.success("OTP resent successfully");
    } catch (error) {
      if (error instanceof NetworkError) {
        toast.error(error.message);
      } else {
        toast.error("Failed to resend OTP. Please try again.");
      }
    }
  };

  const renderOtpInputs = (
    otp: string[],
    refs: React.MutableRefObject<(HTMLInputElement | null)[]>,
    setOtp: (otp: string[]) => void,
    label: string,
    nextRefs?: React.MutableRefObject<(HTMLInputElement | null)[]>,
    prevRefs?: React.MutableRefObject<(HTMLInputElement | null)[]>,
    hasError?: boolean
  ) => (
    <div className="space-y-3">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <div className="flex justify-center gap-2">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (refs.current[index] = el)}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digit}
            onChange={(e) => {
              handleOtpChange(index, e.target.value, otp, setOtp, refs, nextRefs);
              // Clear errors when user starts typing in the respective field
              if (refs === emailOtpRefs && emailOtpError) {
                setEmailOtpError(false);
                // Clear error message if it's related to email OTP
                if (otpError && (otpError.includes("email OTP") || (otpError.includes("OTP") && !otpError.includes("authenticator")))) {
                  setOtpError("");
                }
              } else if (refs === totpRefs && totpInputError) {
                setTotpInputError(false);
                // Clear error message if it's related to authenticator/TOTP
                if (otpError && (otpError.includes("authenticator") || otpError.includes("TOTP"))) {
                  setOtpError("");
                }
              }
            }}
            onKeyDown={(e) => handleKeyDown(index, e, otp, refs, prevRefs, index === 5 && (!nextRefs || !showTotpInput))}
            onPaste={index === 0 ? (e) => handlePaste(e, otp, setOtp, refs) : undefined}
            disabled={isLoading || lockoutTimer > 0}
            className={`w-12 h-14 text-center text-2xl font-bold rounded-lg border-2 bg-background focus:border-primary focus:outline-none transition-colors disabled:opacity-50 ${
              hasError ? "border-destructive" : "border-border"
            }`}
          />
        ))}
      </div>
    </div>
  );

  const getTitle = () => showTotpInput ? "Two-Factor Authentication" : "Verify OTP";

  const getDescription = () => {
    if (showTotpInput) {
      return (
        <>
          Please enter both codes to continue
          <br />
          <span className="font-medium text-foreground">{email}</span>
        </>
      );
    }
    return (
      <>
        Enter the 6-digit code sent to
        <br />
        <span className="font-medium text-foreground">{email}</span>
      </>
    );
  };

  const isVerifyDisabled = isLoading || emailOtp.some((d) => !d) || (showTotpInput && totpCode.some((d) => !d)) || lockoutTimer > 0 || attemptCount >= 5;

  return (
    <>
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <SlikeLogo
              iconClassName="h-10"
              textClassName="text-4xl"
            />
          </div>
          <h1 className="text-3xl font-bold mb-2">{getTitle()}</h1>
          <p className="text-muted-foreground">{getDescription()}</p>
        </div>

        <Card className="glass-card p-6">
          <div className="space-y-6">
            {renderOtpInputs(emailOtp, emailOtpRefs, setEmailOtp, "Email Verification Code", showTotpInput ? totpRefs : undefined, undefined, emailOtpError)}

            {showTotpInput && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">and</span>
                </div>
              </div>
            )}

            {showTotpInput && renderOtpInputs(totpCode, totpRefs, setTotpCode, "Authentication Code (TOTP)", undefined, emailOtpRefs, totpInputError)}

            {otpError && (
              <p className="text-sm text-destructive text-center mt-1 animate-in fade-in-0 slide-in-from-top-1">
                {otpError}
              </p>
            )}

            {lockoutTimer > 0 && (
              <p className="text-sm text-destructive text-center mt-1 animate-in fade-in-0 slide-in-from-top-1">
                Too many attempts. Try again later after {lockoutTimer} seconds.
              </p>
            )}

            {attemptCount >= 5 && (
              <p className="text-sm text-destructive text-center mt-1 animate-in fade-in-0 slide-in-from-top-1">
                Too many failed attempts. Please resend OTP and try with a new code.
              </p>
            )}

            <Button
              onClick={() => handleVerify()}
              className="w-full gradient-primary hover:opacity-90"
              disabled={isVerifyDisabled || lockoutTimer > 0 || attemptCount >= 5}
            >
              {isLoading ? "Verifying..." : "Verify & Continue"}
            </Button>

            <div className="text-center">
              <button
                onClick={handleResend}
                disabled={resendTimer > 0}
                className="text-sm text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
              >
                {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
              </button>
            </div>
          </div>
        </Card>

        <button
          onClick={() => navigate("/")}
          className="flex items-center justify-center w-full mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to login
        </button>
      </div>

      <TOTPVerifyModal
        isOpen={showTOTPModal}
        email={totpUserInfo?.email || email}
        name={totpUserInfo?.name || ""}
        onVerify={handleTOTPVerify}
        onCancel={handleTOTPCancel}
        error={totpError}
        onErrorClear={() => setTotpError(null)}
      />
    </>
  );
};

export default VerifyOTP;
